import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.tsx';
import clsx from 'clsx';
import { PostCardFragmentFragment } from '@/__generated__/graphql.ts';
import { formatDateWithMs } from '../../date-utils/format-date-with-ms.ts';
import { formatDate } from '../../date-utils/format-date.ts';
import {
  DEFAULT_COLLAPSED_BODY_CHARS,
  isStreamDeckSettingEnabled,
  StreamDeckSettings,
} from '../stream-deck/customize-stream-deck-dropdown/use-stream-deck-settings.ts';
import { type HighlightPattern } from '../text-editor/plugins/highlight/types';
import { useHighlightMatchSummary } from '../text-editor/plugins/highlight/use-highlight-match-summary';
import { TextEditor } from '../text-editor/text-editor.tsx';
import { PostCardMedia } from './post-card-media/post-card-media.tsx';
import {
  postCardBody,
  postCardBodyWithDisabledLinks,
  postCardContent,
  postCardDate,
  postCardDeletedPlaceholder,
} from './post-card.css.ts';

export const PostCardContent = ({
  post,
  size = 'medium',
  streamDeckSettings,
  highlightPatterns,
}: {
  post: PostCardFragmentFragment;
  size?: 'small' | 'medium';
  streamDeckSettings?: StreamDeckSettings;
  highlightPatterns?: HighlightPattern[];
}) => {
  const arePostInteractionsEnabled = isStreamDeckSettingEnabled(
    'postInteractions',
    streamDeckSettings,
  );

  const areLinksEnabled = isStreamDeckSettingEnabled(
    'enabledLinks',
    streamDeckSettings,
  );

  const isCreatedDateEnabled = isStreamDeckSettingEnabled(
    'createdDate',
    streamDeckSettings,
  );

  const isDeleted = Boolean(post.deletedAt);

  const compactMaxChars = streamDeckSettings
    ? (streamDeckSettings.maxBodyChars ?? undefined)
    : DEFAULT_COLLAPSED_BODY_CHARS;
  const shouldExpandOnHighlight = streamDeckSettings
    ? streamDeckSettings.expandOnHighlight
    : true;

  const { inHiddenPost: matchesInHiddenPost } = useHighlightMatchSummary({
    postText: post.text,
    patterns: highlightPatterns,
    maxChars: compactMaxChars,
  });

  return (
    <div className={postCardContent}>
      {isDeleted ? (
        <EmptyState
          illustration={<TrashIcon />}
          heading="The post was deleted"
          className={postCardDeletedPlaceholder}
        />
      ) : (
        <>
          <TextEditor
            namespace={
              arePostInteractionsEnabled === false
                ? `post:${post.id}-disabled-post-interactions`
                : `post:${post.id}`
            }
            className={clsx(postCardBody, {
              [postCardBodyWithDisabledLinks]: areLinksEnabled === false,
            })}
            initialState={post.json || post.text}
            editable={false}
            rich
            theme={size === 'small' ? 'small' : 'base'}
            highlightPatterns={highlightPatterns}
            charLimit={compactMaxChars}
            withShowMore={!!compactMaxChars}
            forceExpand={matchesInHiddenPost && shouldExpandOnHighlight}
          />

          <PostCardMedia
            media={post.media}
            authorId={post.author.id}
            createdAt={post.createdAt}
          />
        </>
      )}

      {isCreatedDateEnabled === true && (
        <time
          title={formatDateWithMs(new Date(post.createdAt))}
          dateTime={post.createdAt}
          className={postCardDate}
        >
          {formatDate(new Date(post.createdAt))}
        </time>
      )}
    </div>
  );
};
